import React, { useState, useEffect, useCallback } from 'react';
import { whatsappAPI } from '../../services/api';

const PROVIDERS = [
  { value: 'none',    label: 'None (Disabled)' },
  { value: 'msg91',   label: 'MSG91' },
  { value: 'aisensy', label: 'AiSensy' },
  { value: 'twilio',  label: 'Twilio' },
  { value: 'gupshup', label: 'Gupshup' },
  { value: 'wati',    label: 'WATI' },
];

const PROVIDER_GUIDES = {
  msg91:   'Sign up at msg91.com → WhatsApp → Get API Key + Phone Number ID. Cost: ~₹999/month + Meta fees (₹0.13/utility msg in India)',
  aisensy: 'Sign up at aisensy.com → Get API Key. Cost: ~₹999/month + Meta fees.',
  twilio:  'Sign up at twilio.com → WhatsApp Sandbox → Get Account SID + Auth Token. Cost: $0.005/msg + Meta fees.',
  gupshup: 'Contact provider for API credentials.',
  wati:    'Contact provider for API credentials.',
};

const TEMPLATES = [
  { key: 'student_registration',     label: 'Student Registration Confirmation',   sample: 'Hi {1}, your interest at {2} has been registered. We will contact you shortly!' },
  { key: 'demo_reminder_24h',        label: 'Demo Reminder (24 hours before)',     sample: 'Hi {1}, reminder: your demo is tomorrow at {2}. Join: {3}' },
  { key: 'demo_reminder_1h',         label: 'Demo Reminder (1 hour before)',       sample: 'Hi {1}, your demo starts in 1 hour at {2}. Join now: {3}' },
  { key: 'demo_thankyou',            label: 'Demo Thank You (after completion)',   sample: 'Hi {1}, thank you for attending the {2} demo! Stay tuned for next steps.' },
  { key: 'scholarship_eligible',     label: 'Scholarship Result — Eligible',       sample: 'Congratulations {1}! You scored {2}% and are eligible for the scholarship.' },
  { key: 'scholarship_not_eligible', label: 'Scholarship Result — Not Eligible',  sample: 'Hi {1}, you scored {2}%. Keep trying! Contact us for other options.' },
  { key: 'followup_reminder',        label: 'Follow-up Reminder to Team',          sample: 'Hi {1}, you have a pending follow-up: {2}. Please action it today.' },
  { key: 'lead_assigned',            label: 'Lead Assigned Notification',          sample: 'Hi {1}, lead {2} from {3} has been assigned to you.' },
  { key: 'franchise_confirmation',   label: 'Franchise Inquiry Confirmation',      sample: 'Hi {1}, your franchise inquiry from {2} has been received. We will contact you shortly.' },
  { key: 'outreach_general',         label: 'General Outreach Message',            sample: 'Hi {1}, {2}' },
];

const TOGGLES = [
  { key: 'send_student_registration',   label: 'Student Registration Confirmation' },
  { key: 'send_demo_reminder_24h',      label: 'Demo Reminder (24 hours before)' },
  { key: 'send_demo_reminder_1h',       label: 'Demo Reminder (1 hour before)' },
  { key: 'send_scholarship_result',     label: 'Scholarship Result Notification' },
  { key: 'send_followup_reminder',      label: 'Follow-up Reminder to Team' },
  { key: 'send_lead_assigned',          label: 'Lead Assigned Notification' },
  { key: 'send_franchise_confirmation', label: 'Franchise Inquiry Confirmation' },
  { key: 'send_outreach_messages',      label: 'Allow Team Outreach Messages' },
];

const STATUS_COLORS = { sent: '#3b82f6', delivered: '#22c55e', failed: '#ef4444', queued: '#f59e0b' };

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
        background: checked ? '#22c55e' : '#d1d5db', position: 'relative', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: 8, marginBottom: 16 }}>
      {children}
    </h3>
  );
}

export default function WhatsAppSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState({});
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = useCallback(async () => {
    try {
      const [sRes, lRes, stRes] = await Promise.all([
        whatsappAPI.getSettings(),
        whatsappAPI.getLogs({ limit: 100 }),
        whatsappAPI.getLogStats(),
      ]);
      setSettings(sRes.data.data);
      setLogs(lRes.data.data || []);
      setLogStats(stRes.data.data || {});
    } catch (err) {
      showToast('Failed to load WhatsApp settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      if (apiKeyInput && apiKeyInput !== '****') payload.api_key_encrypted = apiKeyInput;
      delete payload._id; delete payload.__v; delete payload.total_messages_sent;
      delete payload.total_messages_failed; delete payload.last_message_sent_at;
      const res = await whatsappAPI.updateSettings(payload);
      setSettings(res.data.data);
      setApiKeyInput('');
      showToast('WhatsApp settings saved');
    } catch (err) {
      showToast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) return showToast('Enter a phone number', 'error');
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await whatsappAPI.sendTest(testPhone);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.message || 'Test failed' });
    } finally {
      setTestLoading(false);
    }
  };

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));
  const updateTemplate = (key, val) => setSettings(s => ({ ...s, templates: { ...s.templates, [key]: val } }));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 900, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 8, padding: '12px 20px', fontWeight: 600, fontSize: 14,
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>WhatsApp Settings</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Configure your WhatsApp Business messaging provider</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
            background: settings?.is_configured ? '#dcfce7' : '#fee2e2',
            color: settings?.is_configured ? '#16a34a' : '#dc2626',
          }}>
            {settings?.is_configured ? 'Configured' : 'Not Configured'}
          </div>
          <button
            onClick={handleSave} disabled={saving}
            style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Section 1 — Provider Selection */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <SectionTitle>1. Provider Selection</SectionTitle>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>WhatsApp Provider</label>
            <select
              value={settings?.provider || 'none'}
              onChange={e => update('provider', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
            >
              {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 12, paddingTop: 28 }}>
            <Toggle checked={settings?.is_configured || false} onChange={v => update('is_configured', v)} />
            <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Mark as Configured</span>
          </div>
        </div>
        {settings?.provider && settings.provider !== 'none' && PROVIDER_GUIDES[settings.provider] && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', fontSize: 13, color: '#0369a1' }}>
            <strong>Setup Guide: </strong>{PROVIDER_GUIDES[settings.provider]}
          </div>
        )}
      </div>

      {/* Section 2 — Credentials */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <SectionTitle>2. API Credentials</SectionTitle>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>API key is stored encrypted. Leave blank to keep the existing key.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>API Key</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKeyInput}
                onChange={e => setApiKeyInput(e.target.value)}
                placeholder={settings?.api_key_encrypted ? '(key stored — enter new to change)' : 'Enter API Key'}
                style={{ width: '100%', padding: '10px 40px 10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
              <button
                type="button" onClick={() => setShowApiKey(v => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 16 }}
              >{showApiKey ? '🙈' : '👁'}</button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Phone Number ID</label>
            <input value={settings?.phone_number_id || ''} onChange={e => update('phone_number_id', e.target.value)}
              placeholder="e.g. 1234567890"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Business Account ID</label>
            <input value={settings?.business_account_id || ''} onChange={e => update('business_account_id', e.target.value)}
              placeholder="Meta Business Account ID"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Sender Phone Number</label>
            <input value={settings?.sender_phone || ''} onChange={e => update('sender_phone', e.target.value)}
              placeholder="91XXXXXXXXXX (with country code)"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Sender Display Name</label>
            <input value={settings?.sender_name || ''} onChange={e => update('sender_name', e.target.value)}
              placeholder="Nova Era Labs"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
        </div>
      </div>

      {/* Section 3 — Templates */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <SectionTitle>3. Message Templates</SectionTitle>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          Template IDs must be pre-approved by Meta through your provider before they work.
          Paste the approved template name/ID from your provider dashboard.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {TEMPLATES.map(t => (
            <div key={t.key} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16, alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', borderRadius: 4, padding: '4px 8px', lineHeight: 1.5 }}>{t.sample}</div>
              </div>
              <input
                value={settings?.templates?.[t.key] || ''}
                onChange={e => updateTemplate(t.key, e.target.value)}
                placeholder="Template ID or name"
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Section 4 — Feature Toggles */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <SectionTitle>4. Feature Toggles</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {TOGGLES.map(t => (
            <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 8 }}>
              <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{t.label}</span>
              <Toggle checked={settings?.[t.key] || false} onChange={v => update(t.key, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Section 5 — Test Message */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <SectionTitle>5. Test Message</SectionTitle>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Test Phone Number (with country code)</label>
            <input
              value={testPhone} onChange={e => setTestPhone(e.target.value)}
              placeholder="91XXXXXXXXXX"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
          <button
            onClick={handleTest} disabled={testLoading}
            style={{ background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14, height: 42 }}
          >{testLoading ? 'Sending...' : 'Send Test Message'}</button>
        </div>
        {testResult && (
          <div style={{
            marginTop: 14, padding: '12px 16px', borderRadius: 8, fontSize: 13,
            background: testResult.success ? '#dcfce7' : '#fee2e2',
            color: testResult.success ? '#16a34a' : '#dc2626',
            border: `1px solid ${testResult.success ? '#86efac' : '#fca5a5'}`,
          }}>
            {testResult.success
              ? `✓ Message sent! Message ID: ${testResult.messageId || 'N/A'}`
              : `✗ Failed: ${testResult.error}`}
          </div>
        )}
        {settings?.last_test_sent_at && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
            Last test: {new Date(settings.last_test_sent_at).toLocaleString('en-IN')} — Status: {settings.last_test_status}
            {settings.last_test_error && ` — ${settings.last_test_error}`}
          </div>
        )}
      </div>

      {/* Section 6 — Message Log */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
        <SectionTitle>6. Message Log</SectionTitle>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Sent', value: logStats.total || 0, color: '#6366f1' },
            { label: 'Delivered', value: logStats.delivered || 0, color: '#22c55e' },
            { label: 'Failed', value: logStats.failed || 0, color: '#ef4444' },
            { label: 'Delivery Rate', value: `${logStats.delivery_rate || 0}%`, color: '#0ea5e9' },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 120, padding: '14px 20px', background: '#f8fafc', borderRadius: 10, textAlign: 'center', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No messages sent yet</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Recipient', 'Template', 'Status', 'Timestamp', 'Sent By'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.recipient_name || '—'}</div>
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>{log.recipient_phone}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{log.template_key || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        background: `${STATUS_COLORS[log.status]}20`, color: STATUS_COLORS[log.status],
                      }}>{log.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN') : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>
                      {log.is_automated ? <span style={{ color: '#6366f1', fontSize: 12 }}>Automated</span> : (log.sent_by?.name || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
